/**
 * useChartWidgetShell + ChartShell — shared chrome for every chart widget
 * (Epic 37 redesign). One place for: the quiet uppercase title, consistent
 * padding, loading/error/empty/placeholder states, AND the size-aware `compact`
 * flag (measures the widget's own rendered height; below a threshold it drops
 * chrome so the chart doesn't overlap itself — this is what fixed the mush in the
 * scaled sidebar-preview frame). Charts call `useChartWidgetShell` for `compact`
 * + the `shell()` wrapper, keeping each widget focused on its chart.
 *
 * Navy is the report's primary chart hue (per design direction); the shared
 * palette lives here so line/bar/pie/etc. read as one family.
 */

import { createContext, useContext, useRef, useState, useEffect, type ReactNode, type RefObject } from "react";
import { Warning } from "@phosphor-icons/react";
import { platformBadge } from "../../../lib/integrations";
import { METRIC_PLATFORM_MAP } from "../../../lib/templateUtils";
import { platformDisplayName } from "@provenleap/integrations";
import type { WidgetAccountContribution } from "../../../lib/widgetData";

/**
 * Story 32-7 — fetch-time account-contribution warning context. Provided by
 * WidgetRenderer from the widget's data envelope (`data.accountContribution`,
 * server-computed from per-integration fetch outcomes). Chip visibility =
 * field presence in the RESPONSE — config-time attachment counts
 * (useClientIntegrations / config.integrationIds) contribute NOTHING here,
 * so the warning always reflects the current scope/health state and
 * auto-clears on the next fully-contributing populate (AC #8).
 */
export const WidgetAccountContributionContext = createContext<
  WidgetAccountContribution | undefined
>(undefined);

/** Below this rendered height, drop chrome (legend / title / center labels) so
 *  the chart never overlaps itself — covers the ~108px scaled preview frame. */
export const COMPACT_HEIGHT_PX = 150;

/** Navy monochrome series palette (dark → light). Cycles for extra series. */
export const NAVY_SERIES = [
  "hsl(222 47% 24%)",
  "hsl(222 45% 34%)",
  "hsl(221 43% 45%)",
  "hsl(217 40% 56%)",
  "hsl(214 38% 67%)",
  "hsl(213 35% 78%)",
];

/** Compact number (1.2K / 3.4M) for tight labels. */
export function compactNumber(n: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

export interface ChartWidgetShell {
  /** True when the rendered box is short (preview frame / tiny widget). */
  compact: boolean;
  /** Measured rendered width of the widget box in px (0 before first layout /
   *  in jsdom). Used by charts to decide axis-label tilting against real space. */
  width: number;
  /** Measured rendered height of the widget box in px (0 before first layout /
   *  in jsdom). Used to cap how much vertical room axis gutters may claim. */
  height: number;
  /** Attach to the outer element so its height can be measured. */
  ref: RefObject<HTMLDivElement | null>;
  /** Wrap chart content in the shared card chrome (title + padding + states). */
  shell: (
    type: string,
    title: string,
    body: ReactNode,
    opts?: { centered?: boolean; keepTitle?: boolean; titleStyle?: WidgetTitleStyle },
  ) => ReactNode;
}

/** Per-widget title styling (Appearance > Title). Empty fields fall back to the
 *  default quiet-uppercase-muted look. */
export interface WidgetTitleStyle {
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  align?: "left" | "center" | "right";
}

/** Extract the per-widget title style from `config.style.title` for the shell. */
export function widgetTitleStyle(config: Record<string, unknown> | undefined): WidgetTitleStyle | undefined {
  const t = (config?.style as { title?: WidgetTitleStyle } | undefined)?.title;
  return t && typeof t === "object" ? t : undefined;
}

/** Resolve a metric's platform: explicit per-metric pin → canonical map. */
function metricPlatform(config: Record<string, unknown> | undefined, metric: string): string | undefined {
  const sidecar = (config?.metricIntegrations ?? {}) as Record<string, string>;
  return sidecar[metric] || METRIC_PLATFORM_MAP[metric];
}

/**
 * Every DISTINCT platform feeding a widget, for the integration badge. From the
 * chart's metrics (pinned → canonical), preserving first-seen order. Falls back
 * to `config.integrationId` for widgets without a metrics array (e.g. kpi/pie).
 */
function widgetBadgePlatforms(config: Record<string, unknown> | undefined): string[] {
  const metrics = Array.isArray(config?.metrics) ? (config!.metrics as unknown[]) : [];
  const seen: string[] = [];
  for (const m of metrics) {
    const key = typeof m === "string" ? m : (m && typeof m === "object" ? (m as { key?: string }).key : undefined);
    if (!key) continue;
    const p = metricPlatform(config, key);
    if (p && !seen.includes(p)) seen.push(p);
  }
  if (seen.length > 0) return seen;
  const single = config?.integrationId;
  return typeof single === "string" && single ? [single] : [];
}

/**
 * For a chart whose metrics span MORE THAN ONE integration, returns a function
 * that appends " · <Channel name>" to each series label so the legend shows which
 * integration each line/bar comes from (e.g. "cost · Facebook Ads"). When all
 * metrics share one platform it returns a no-op (clean single-source legend).
 */
export function seriesChannelSuffix(
  config: Record<string, unknown> | undefined,
  metricKeys: string[],
): (metric: string) => string {
  const platforms = new Set(metricKeys.map((m) => metricPlatform(config, m)).filter(Boolean) as string[]);
  if (platforms.size <= 1) return () => "";
  return (metric: string) => {
    const p = metricPlatform(config, metric);
    return p ? ` · ${platformDisplayName(p)}` : "";
  };
}

export function useChartWidgetShell(config?: Record<string, unknown>): ChartWidgetShell {
  const ref = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only flip compact on a REAL positive measurement under the threshold; a
    // height of 0 means "not laid out yet" (jsdom / first paint) → treat as full.
    const update = () => {
      const rect = el.getBoundingClientRect();
      setCompact(rect.height > 0 && rect.height < COMPACT_HEIGHT_PX);
      setWidth(rect.width);
      setHeight(rect.height);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Title style comes from the widget config (Appearance > Title); an explicit
  // opts.titleStyle still wins as an override.
  const configTitleStyle = widgetTitleStyle(config);
  const subtitle = typeof config?.subtitle === "string" ? config.subtitle.trim() : "";

  // Integration badge: which data source(s) feed this widget. Sits on the SAME row
  // as the title (left: title, right: badge). Mode from Appearance > Source:
  // "logo" (default) / "logo-name" / "none". Own text style (color/font/size) +
  // icon size — config.style.badge. No background (sits on the card colour).
  //
  // MULTI-INTEGRATION: when the chart's metrics span more than one platform, the
  // single config.integrationId is meaningless — so we show ALL involved logos
  // (no name, since there are several). Single-platform keeps logo + optional name.
  const badgeStyle = ((config?.style as { badge?: { color?: string; fontFamily?: string; fontSize?: number; iconSize?: number } } | undefined)?.badge ?? {}) as {
    color?: string;
    fontFamily?: string;
    fontSize?: number;
    iconSize?: number;
  };
  const badgeIconPx = typeof badgeStyle.iconSize === "number" ? badgeStyle.iconSize : 20;
  const badgeMode = ((config?.style as { integrationBadge?: string } | undefined)?.integrationBadge ?? "logo") as
    | "logo-name"
    | "logo"
    | "none";
  // Resolve every distinct platform feeding the chart (metric-pinned → canonical),
  // falling back to config.integrationId for non-metric widgets (e.g. kpi).
  const badgePlatforms = widgetBadgePlatforms(config);
  const badges = badgePlatforms.map((p) => platformBadge(p)).filter(Boolean) as { displayName: string; logoSrc: string }[];
  const multiSource = badges.length > 1;
  // Show the badge whenever there's a source + mode != none — including on compact
  // cards like KPIs (it's a small top-right logo and doesn't crowd the value).
  const showBadge = badges.length > 0 && badgeMode !== "none";
  const badgeNode = showBadge ? (
    <div
      data-testid="widget-integration-badge"
      title={badges.map((b) => b.displayName).join(" · ")}
      className="flex shrink-0 items-center gap-1.5"
    >
      {badges.map((b) => (
        <img key={b.logoSrc} src={b.logoSrc} alt="" style={{ width: badgeIconPx, height: badgeIconPx }} className="shrink-0 rounded-sm" />
      ))}
      {/* Name only on a single source (multiple names would clutter). */}
      {badgeMode === "logo-name" && !multiSource && (
        <span
          className={`max-w-[160px] truncate font-medium ${badgeStyle.color ? "" : "text-muted-foreground"} ${badgeStyle.fontSize ? "" : "text-[11px]"}`}
          style={{
            ...(badgeStyle.color ? { color: badgeStyle.color } : {}),
            ...(badgeStyle.fontFamily ? { fontFamily: badgeStyle.fontFamily } : {}),
            ...(badgeStyle.fontSize ? { fontSize: badgeStyle.fontSize } : {}),
          }}
        >
          {badges[0]!.displayName}
        </span>
      )}
    </div>
  ) : null;

  // Story 32-7 — amber "K of N accounts contributed" warning chip. Rendered
  // ONLY when the populate response carried accountContribution (server
  // attach rule: error === null && consulted ≥ 2 && contributed < consulted).
  // Mock/preview envelopes never carry the field → no chip, automatically.
  const accountContribution = useContext(WidgetAccountContributionContext);
  let contributionChip: ReactNode = null;
  if (accountContribution) {
    const { contributed, consulted, byPlatform } = accountContribution;
    // Tooltip: per-platform breakdown via display names ONLY — never provider
    // error strings (raw provider messages are platformStatus territory and
    // may carry vendor-internal text).
    const breakdown =
      byPlatform.length > 0
        ? byPlatform
            .map(
              (p) =>
                `${platformDisplayName(p.platform)}: ${p.contributed} of ${p.consulted} accounts contributed`,
            )
            .join(" · ")
        : `${contributed} of ${consulted} accounts contributed`;
    const tooltip = `${breakdown}. Accounts that could not be fetched are missing from this widget's numbers.`;
    const chipCopy = `${contributed} of ${consulted} accounts contributed`;
    contributionChip = compact ? (
      // Compact preview frames (< 150px) have a 1-line title row: icon only.
      // Review R1-1: icon-only mode has no visible text, so it carries an
      // explicit accessible name (the chip copy; full detail stays in title).
      <span
        data-testid="widget-account-contribution-warning"
        title={tooltip}
        role="img"
        aria-label={chipCopy}
        className="inline-flex shrink-0 items-center"
      >
        <Warning size={14} weight="bold" className="text-amber-500" />
      </span>
    ) : (
      // Amber pill — TemplateCard "Missing integrations" badge recipe.
      <span
        data-testid="widget-account-contribution-warning"
        title={tooltip}
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
      >
        <Warning size={12} weight="bold" />
        {chipCopy}
      </span>
    );
  }

  const shell: ChartWidgetShell["shell"] = (type, title, body, opts) => {
    const ts = opts?.titleStyle ?? configTitleStyle;
    return (
    <div
      ref={ref}
      data-testid={`widget-type-${type}`}
      // Inline styles for the core layout (height/flex/centering) so it can NEVER
      // be defeated by a CSS-source/utility-generation gap — every widget must
      // fill its cell and (when centered) center its content reliably.
      style={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, width: "100%" }}
      className={compact ? "p-2" : "p-4"}
    >
      {/* Title row: title (left) + contribution warning + integration badge
          (right) on the SAME line, vertically aligned. The badge shows which
          data source feeds the widget (incl. compact cards like KPIs). When
          there's no title the badge/chip still sit on this row (pushed right).
          Story 32-7: a title-less widget must still show the warning chip. */}
      {(title || badgeNode || contributionChip) && (
        <div className={`flex items-center justify-between gap-2 ${compact ? "mb-1" : "mb-2"}`}>
          {title ? (
            <h3
              className={`min-w-0 flex-1 truncate font-medium tracking-wide ${
                ts?.color ? "" : "text-muted-foreground"
              } ${ts?.fontSize ? "" : "text-[13px]"} ${
                // Keep the signature uppercase unless the user picked a custom font/size
                // (then respect the text as-typed — uppercasing a chosen font is jarring).
                ts?.fontSize || ts?.fontFamily ? "" : "uppercase"
              }`}
              style={{
                ...(ts?.color ? { color: ts.color } : {}),
                ...(ts?.fontSize ? { fontSize: ts.fontSize } : {}),
                ...(ts?.fontFamily ? { fontFamily: ts.fontFamily } : {}),
                textAlign: ts?.align ?? "left",
              }}
            >
              {title}
            </h3>
          ) : (
            <span className="min-w-0 flex-1" />
          )}
          {/* Story 32-7 — between the title and the badge, never truncated
              (shrink-0); the title keeps min-w-0 flex-1 truncate. */}
          {contributionChip}
          {badgeNode}
        </div>
      )}
      {/* Optional subtitle — a quiet second line under the title. */}
      {subtitle && (
        <p
          className={`truncate text-[11px] text-muted-foreground ${compact ? "-mt-0.5 mb-1" : "-mt-1 mb-2"}`}
          style={{ textAlign: ts?.align ?? "left" }}
        >
          {subtitle}
        </p>
      )}
      <div
        style={{
          display: "flex",
          flex: "1 1 0%",
          minHeight: 0,
          flexDirection: "column",
          ...(opts?.centered ? { alignItems: "center", justifyContent: "center", textAlign: "center" } : {}),
        }}
      >
        {body}
      </div>
    </div>
    );
  };

  return { compact, width, height, ref, shell };
}

/** Centered muted message (no-data / placeholder / error) inside the shell body. */
export function ChartMessage({ children, role, testId }: { children: ReactNode; role?: "alert"; testId?: string }) {
  return (
    <span role={role} className={`text-sm ${role === "alert" ? "text-destructive" : "text-muted-foreground"}`} data-testid={testId}>
      {children}
    </span>
  );
}
